import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Users.css';

const Users = ({ userData }) => {
  const [activeTab, setActiveTab] = useState('teachers');
  const [subTab, setSubTab] = useState('active');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        let url = '';
        
        if (activeTab === 'teachers') {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/admin/teachers/${subTab}`;
        } else if (activeTab === 'students') {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/admin/students/st/${subTab}`;
        } else if (activeTab === 'demorequest') {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/admin/students/demorequest`;
        } else if (activeTab === 'onlineusers') {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/admin/students/online`;
        } else if (activeTab === 'studentsupdate') {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/admin/students/studentsupdate`;
        }

        const response = await axios.get(url, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('userData')}` }
        });

        const data = response.data.data || response.data;
        
        if (activeTab === 'onlineusers') {
          setOnlineUsers(data);
        } else {
          setUsers(data);
          setFilteredUsers(data);
        }
        
        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        setUsers([]);
        setFilteredUsers([]);
        setOnlineUsers([]);
      } finally {
        setLoading(false);
      }
    };

    if (userData?.id) {
      fetchUsers();
      
      // Set up polling for online users if that tab is active
      if (activeTab === 'onlineusers') {
        const interval = setInterval(fetchUsers, 30000); // Refresh every 30 seconds
        return () => clearInterval(interval);
      }
    }
  }, [activeTab, subTab, userData]);

  useEffect(() => {
    if (searchTerm) {
      const lowerCaseTerm = searchTerm.toLowerCase();
      const sourceData = activeTab === 'onlineusers' ? onlineUsers : users;
      const filtered = sourceData.filter(({ FirstName, LastName, Email, IdUser }) =>
        [FirstName, LastName, Email].some(field => 
          field?.toLowerCase().includes(lowerCaseTerm)
        ) || IdUser?.toString().includes(searchTerm));
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(activeTab === 'onlineusers' ? onlineUsers : users);
    }
  }, [searchTerm, users, onlineUsers, activeTab]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never signed in';
    return new Date(dateString).toLocaleString();
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleApproveClick = async (request) => {
    setSelectedRequest(request);
    setModalLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/students/demoapprove/${request.ClassDateTime}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('userData')}` } }
      );
      setAvailableTeachers(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setModalLoading(false);
    }
    setShowModal(true);
  };

  const handleApproveSubmit = async () => {
    if (!selectedTeacher) {
      alert('Please select a teacher');
      return;
    }
    setIsSubmitting(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/students/demoapproved`,
        {
          IdUser: selectedTeacher,
          Id: selectedRequest.Id
        },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('userData')}` } }
      );
      // Refresh the demo requests list
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/students/demorequest`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('userData')}` } }
      );
      setUsers(response.data);
      setFilteredUsers(response.data);
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="users-management__error">
        <p>Error: {error}</p>
        <button 
          className="users-management__retry-btn" 
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="users-management">
      <h2 className="users-management__title">Users Management</h2>
      
      <div className="users-management__tabs">
        <button
          className={`users-management__tab ${activeTab === 'teachers' ? 'users-management__tab--active' : ''}`}
          onClick={() => {
            setActiveTab('teachers');
            setSubTab('active');
            setSearchTerm('');
          }}
        >
          Teachers
        </button>
        <button
          className={`users-management__tab ${activeTab === 'students' ? 'users-management__tab--active' : ''}`}
          onClick={() => {
            setActiveTab('students');
            setSubTab('paid');
          }}
        >
          Students
        </button>
        
        

        <button
          className={`users-management__tab ${activeTab === 'studentsupdate' ? 'users-management__tab--active' : ''}`}
          onClick={() => {
            setActiveTab('studentsupdate');
            setSearchTerm('');
          }}
        >
          Students needs update
        </button>
      </div>
      
      {activeTab === 'teachers' && (
        <div className="users-management__sub-tabs">
          <button
            className={`users-management__sub-tab ${subTab === 'active' ? 'users-management__sub-tab--active' : ''}`}
            onClick={() => setSubTab('active')}
          >
            Active
          </button>
          <button
            className={`users-management__sub-tab ${subTab === 'inactive' ? 'users-management__sub-tab--active' : ''}`}
            onClick={() => setSubTab('inactive')}
          >
            Inactive
          </button>
        </div>
      )}
      
      {activeTab === 'students' && (
        <>
          <div className="users-management__sub-tabs">
            <button
              className={`users-management__sub-tab ${subTab === 'paid' ? 'users-management__sub-tab--active' : ''}`}
              onClick={() => setSubTab('paid')}
            >
              Paid
            </button>
            <button
              className={`users-management__sub-tab ${subTab === 'demo' ? 'users-management__sub-tab--active' : ''}`}
              onClick={() => setSubTab('demo')}
            >
              Demo
            </button>
          </div>
          <div className="users-management__search">
            <input
              type="text"
              placeholder="Search students by name, email or ID..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="users-management__search-input"
            />
          </div>
        </>
      )}

      {activeTab === 'onlineusers' && (
        <div className="users-management__search">
          <input
            type="text"
            placeholder="Search online users by name, email or ID..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="users-management__search-input"
          />
        </div>
      )}
      
      <div className="users-management__content">
        {loading ? (
          <div className="users-management__loading">
            <div className="users-management__spinner"></div>
            <p>Loading {activeTab} data...</p>
          </div>
        ) : (activeTab === 'onlineusers' ? onlineUsers : filteredUsers).length > 0 ? (
          <div className="users-management__table-container">
            <table className="users-management__table">
              <thead className="users-management__table-header">
                <tr>
                  {activeTab === 'onlineusers' ? (
                    <>
                      <th>Name</th>
                      <th>Last SignIn</th>

                      <th>Last Activity</th>
                      <th>Online Status</th>
                      <th>Actions</th>

                    </>
                  ) : activeTab === 'studentsupdate' ? (
                    <>
                      <th>Name</th>
                      <th>Last Activity</th>
                      <th>Online Status</th>
                      <th>Actions</th>
                    </>
                  ) : activeTab === 'demorequest' ? (
                    <>
                      <th>Name</th>
                      <th>Age</th>
                      <th>Parent's Email</th>
                      <th>Mobile</th>
                      <th>Class Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </>
                  ) : (
                    <>
                      <th>Name</th>
                      <th>LastSign</th>                      
                      <th>Last Seen</th>
                      <th>Status</th>
                      <th>Online Status</th>
                      <th>Actions</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="users-management__table-body">
                {(activeTab === 'onlineusers' ? onlineUsers : filteredUsers).map(user => (
                  <tr key={user.IdUser || user.Id} className="users-management__table-row">
                    <td>{user.FirstName} {user.LastName}</td>
                    
                    {activeTab === 'onlineusers' ? (
                      <>
                        <td>{formatDate(user.Lastsignin)}</td>
                        <td>{formatDate(user.LastSeen)}</td>
                        <td className={user.IsOnline ? 'users-management__status--online' : 'users-management__status--offline'}>
                          <span className="status-indicator">
                            {user.IsOnline ? 'Online' : 'Offline'}
                          </span>
                        </td>
                        <td>
                          <button 
                            className="users-management__profile-btn"
                            onClick={() => {
                              const profileUrl = `studentprofile/${user.IdUser}`;
                              window.open(profileUrl, '_blank');
                            }}
                          >
                            Student Profile
                          </button>
                        </td>                        
                      </>
                    ) : activeTab === 'studentsupdate' ? (
                      <>
                        <td>{formatDate(user.LastSeen)}</td>
                        <td className={user.IsOnline ? 'users-management__status--online' : 'users-management__status--offline'}>
                          <span className="status-indicator">
                            {user.IsOnline ? 'Online' : 'Offline'}
                          </span>
                        </td>
                        <td>
                          <button 
                            className="users-management__profile-btn"
                            onClick={() => {
                              const profileUrl = `studentprofile/${user.IdUser}`;
                              window.open(profileUrl, '_blank');
                            }}
                          >
                            Student Profile
                          </button>
                        </td>
                      </>
                    ) : activeTab === 'demorequest' ? (
                      <>
                        <td>{user.Age}</td>
                        <td>{user.ParentsEmail}</td>
                        <td>
                          <a href={`https://wa.me/${user.MobileNumber}`} target="_blank" rel="noopener noreferrer">
                            {user.MobileNumber}
                          </a>
                        </td>                        
                        <td>{new Date(user.ClassDateTime).toLocaleString()}</td>
                        <td className={user.approved === 1 ? 'users-management__status--active' : 'users-management__status--demo'}>
                          {user.approved === 1 ? 'Approved' : 'Pending'}
                        </td>
                        <td>
                          {user.approved === 0 && (
                            <button 
                              className="users-management__approve-btn"
                              onClick={() => handleApproveClick(user)}
                            >
                              Approve
                            </button>
                          )}
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{formatDate(user.Lastsignin)}</td>
                        <td>{formatDate(user.LastSeen)}</td>
                        <td className={
                          activeTab === 'teachers' 
                            ? (user.IsActive === true 
                              ? 'users-management__status--active' 
                              : 'users-management__status--inactive')
                            : (user.Type === 0 
                              ? 'users-management__status--paid' 
                              : 'users-management__status--demo')
                        }>
                          {activeTab === 'teachers' 
                            ? (user.IsActive === true ? 'Active' : 'Inactive')
                            : (user.Type === 0 ? 'Paid' : 'Demo')}
                        </td>
                        <td className={user.IsOnline? 'users-management__status--active' : 'users-management__status--inactive'}>{user.IsOnline? "Online" : "Offline"}</td>

                        <td>
                          <button 
                            className="users-management__profile-btn"
                            onClick={() => {
                              const profileUrl = activeTab === 'teachers' 
                                ? `teacherprofile/${user.IdUser}`
                                : `studentprofile/${user.IdUser}`;
                              window.open(profileUrl, '_blank');
                            }}
                          >
                            {activeTab === 'teachers' ? 'Teacher Profile' : 'Student Profile'}
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="users-management__empty">
            {searchTerm 
              ? `No ${activeTab === 'onlineusers' ? 'online users' : subTab + ' ' + activeTab} match your search` 
              : `No ${activeTab === 'onlineusers' ? 'users currently online' : 
                 activeTab === 'demorequest' ? 'demo requests' : subTab + ' ' + activeTab} found`}
          </div>
        )}
      </div>
      
      {/* Modal for approving demo requests */}
      {showModal && (
        <div className="users-management__modal">
          <div className="users-management__modal-content">
            <h3>Approve Demo Request</h3>
            {modalLoading ? (
              <div className="users-management__loading">
                <div className="users-management__spinner"></div>
                <p>Loading available teachers...</p>
              </div>
            ) : (
              <>
                <div className="users-management__modal-details">
                  <p><strong>Student:</strong> {selectedRequest.FirstName} {selectedRequest.LastName}</p>
                  <p><strong>Age:</strong> {selectedRequest.Age}</p>
                  <p><strong>Parent's Email:</strong> {selectedRequest.ParentsEmail}</p>
                  <p><strong>Mobile:</strong> {selectedRequest.MobileNumber}</p>
                  <p><strong>Class Date:</strong> {new Date(selectedRequest.ClassDateTime).toLocaleString()}</p>
                </div>
                
                <div className="users-management__modal-teachers">
                  <h4>Available Teachers:</h4>
                  {availableTeachers.length > 0 ? (
                    availableTeachers.map(teacher => (
                      <div key={teacher.IdUser} className="users-management__teacher-option">
                        <input
                          type="radio"
                          id={`teacher-${teacher.IdUser}`}
                          name="teacher"
                          value={teacher.IdUser}
                          checked={selectedTeacher === teacher.IdUser}
                          onChange={() => setSelectedTeacher(teacher.IdUser)}
                        />
                        <label htmlFor={`teacher-${teacher.IdUser}`}>
                          {teacher.FirstName} {teacher.LastName} (Sessions taken: {teacher.sessionsTaken})
                        </label>
                      </div>
                    ))
                  ) : (
                    <p>No teachers available for this time slot.</p>
                  )}
                </div>
                
                <div className="users-management__modal-actions">
                  <button
                    className="users-management__modal-cancel"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="users-management__modal-approve"
                    onClick={handleApproveSubmit}
                    disabled={isSubmitting || !selectedTeacher || availableTeachers.length === 0}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Approving...
                      </>
                    ) : (
                      'Approve'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;